import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Card, Ringil3BadgeRow } from '@components/ringil3/styles';
import Heading from '@components/ringil3/Heading/Heading';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import {
  AuditActionEnum,
  AuditEntityTypeEnum,
  CodeTypeEnum,
  ComputeReservationDurationConfiguration,
  DefaultVersionEnum,
  EntityTypeEnum,
  FeaturesEnum,
  PermissionTypesEnum,
  ReservationActionTypesEnum,
  ReservationDetailOutDTO,
  ReservationFlagTypeEnum,
} from '@api/ringil-api.v2';
import { formatLocationNotino, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SvgAuditlog from '@components/ringil3/icons/heading/Auditlog';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces';
import LinkedExpBadge from '@components/ringil3/Badges/LinkedExpBadge';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge';
import UpdatedBadge from '@components/ringil3/Badges/UpdatedBadge';
import ReservationNameTag from '@components/ringil3/Features/Reservation/Tags/ReservationNameTag';
import CustomerNameTag from '@components/ringil3/Features/Reservation/Tags/CustomerNameTag';
import ReservationNoteTag from '@components/ringil3/Features/Reservation/Tags/ReservationNoteTag';
import SvgLkw from '@components/ringil3/icons/heading/Lkw';
import SelectedCarrierTag from '@components/framework/tags/SelectedCarrierTag.tsx';
import CarPlateTag from '@components/framework/tags/CarPlateTag.tsx';
import DriverAndIdTag from '@components/framework/tags/DriverAndIdTag.tsx';
import PhoneTag from '@components/framework/tags/PhoneTag.tsx';
import ReservationCarrierNote from '@components/ringil3/Features/Reservation/Tags/ReservationCarrierNote';
import SvgReservation from '@components/ringil3/icons/heading/Reservation';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3';
import { ColWithGap, ColWithSmallerGap } from '@components/styles';
import RingilOrder from '@components/ringil3/RingilOrder/RingilOrder';
import SvgCalendar from '@components/ringil3/icons/Calendar';
import SvgMappoint from '@components/ringil3/icons/Mappoint';
import SvgReservationManipulation from '@icons/LoadUp.svg?react';
import {
  useCreateEditReservationSlotSidebar,
} from '@components/ringil3/Features/Reservation/Sidedbars/EditReservationSlot';
import {
  useCreateEditReservationOrdersSidebar,
} from '@components/ringil3/Features/Reservation/Sidedbars/EditReservationOrders.tsx';
import ReservationMoreButtons from '@components/ringil3/Features/Reservation/MoreButton/ReservationMoreButtons';
import SvgReservationPin from '@components/ringil3/icons/reservation/ReservationPin';
import dayjs from 'dayjs';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import { useCreateChangeSlotDuration } from '@components/obsolete/timeslot/ChangeSlotSidebar.tsx';
import { useDuplicateTimeslot } from '@components/obsolete/timeslot/duplicate/duplicate.tsx';
import { useCreateRingilReservationTabs } from '@components/ringil3/Features/Reservation/Tabs/ReservationTabs.tsx';
import ETransport from '@components/features/eTransport/ETransport.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import {
  useCreateEditReservationOrderItemsDiscrepancySidebar,
} from '@components/ringil3/Features/Reservation/Sidedbars/EditOrderItemsDiscrepancy.tsx';
import DiscrepancyEditHeading3 from '@components/ringil3/Heading/DiscrepancyEditHeading3.tsx';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import {
  dayjsFromApiUtcInTz,
  formatDateTimeInTzWithLocale,
  stringFromTimeslotResponse,
} from '@utils/dateWithTz.tsx';
import AuditLogHighlights from '@components/ringil3/AuditLogHighlights/AuditLogHighlights.tsx';
import TruckTag from '@components/framework/tags/TruckTag.tsx';
import SvgExclamationFilled from '@icons/circle-exclamation-filled.svg?react';
import WorkflowStateTag from '@components/workflow/WorkflowStateTag.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import ReservationFlagsTag from '@components/RingilComponents/tags/ReservationFlagsTag.tsx';
import { toEditableFlags } from '@components/RingilComponents/form/flagUnitsLimit';
import InternalInfoCatalogTag from '@components/framework/tags/InternalInfoCatalogTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { useLocationSupersedeState } from '@components/ringil3/Features/Reservation/useLocationSupersedeState';

interface ComponentProps {
  reservationId: string;
  /**
   * Shown inside another page (the reservation list's split view, RD-2957): no breadcrumbs, the heading
   * drops to h2 with the number linking to the full page, and a deleted reservation stays in the list
   * page instead of navigating to it. Everything else is the full detail.
   */
  embedded?: boolean;
  /** Called after the detail reloaded itself following a change (or was deleted) — e.g. to refresh the list beside it. */
  onChanged?: () => void;
}

const ReservationDetail: React.FC<ComponentProps> = ({ reservationId, embedded = false, onChanged }) => {
  const { tr } = useTranslationLgs();
  const {
    getReservationDetail,
    manualWebhookReservationAvailability,
    reservationUpdateCarPlate,
    reservationUpdateDriverNameAndID,
    reservationUpdateDriverPhone,
    reservationUpdateNoteForCarrier,
    reservationUpdateNote,
    reservationUpdateInternalNote,
    reservationUpdateFolders,
    reservationUpdateReservationName,
    isEntityManagedByWorkflow,
    updateReservationCarrierEntity,
    reservationNotifyDriver,
    reservationUpdateTruck,
    getCreateEditReservationSettings,
    updateReservationFlags,
    updateReservationOnBehalfOf,
  } = useContext(ApiContext);
  const [reservation, setReservation] = useState<ReservationDetailOutDTO>();
  const [webhookAvailable, setWebhookAvailable] = useState(false);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);
  const [isManagedByWorkflow, setIsManagedByWorkflow] = useState(false);
  const [settings, setSettings] = useState<ComputeReservationDurationConfiguration>();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { showToastMessage } = useContext(ToastContext);

  const { selectedPanel, renderTabs } = useCreateRingilReservationTabs();

  const { EditReservationSlotSidebar, setVisible, visible } = useCreateEditReservationSlotSidebar();
  const { CreateTimeslotSidebar, createTimeslotRequest, setCreateTimeslotRequest } = useCreateTimeslotSidebar();

  /**
   * Whether the reservation's location still takes new reservations: a superseded one forwards to its
   * replacement, a plainly deleted one takes none at all (RD-2872).
   */
  const locationState = useLocationSupersedeState(reservation?.locationId, reservation?.customerId);

  /**
   * Location every copy of this reservation is created on: its own, or the replacement when that
   * location has since been superseded. A superseded location is archived, so the create sidebar's
   * picker no longer offers it and the prefilled value would render as an empty field. The ramp is
   * not carried over — the user picks one at the replacement.
   */
  const copyTargetLocationId = locationState.replacement?.id ?? reservation?.locationId;

  /** Opens the "duplicate reservation" sidebar. */
  const openDuplicate = () => {
    setCreateTimeslotRequest({
      applicationId: null,
      companyLocationId: null,
      preferredDate: '',
      companyLocationCustomerId: null,
      expeditionLocationId: null,
      expeditionId: reservation.linkedToExpCoords?.expeditionId,
      reservationId: reservation.id,
      timezoneId: reservation.slot?.timezoneId,
    });
  };

  const editFlag = useSecuredFlag('reservation-detail-edit', {
    kind: 'editability',
    description: 'Edit reservation detail tags and fields',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DETAIL_RW],
  });
  // Audit log is visible to any party holding the read permission (owner or carrier)
  // the BE endpoint row-filters the audit to what the caller may see.
  const auditReadFlag = useSecuredFlag('reservation-detail-audit-read', {
    kind: 'visibility',
    description: 'View reservation audit log',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DETAIL_AUDITLOG_READ],
  });
  const editStatusFlag = useSecuredFlag('reservation-detail-edit-status', {
    kind: 'editability',
    description: 'Change reservation workflow state',
    permissions: [
      PermissionTypesEnum.PERMISSION_TIMESLOT_DETAIL_RW,
      PermissionTypesEnum.PERMISSION_TIMESLOT_CHANGE_STATUS,
    ],
  });
  const editInternalNoteFlag = useSecuredFlag('reservation-detail-edit-internal-note', {
    kind: 'editability',
    description: 'Edit reservation internal note',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_INTERNALNOTE_RW],
  });
  const deleteFlag = useSecuredFlag('reservation-detail-delete', {
    kind: 'action',
    description: 'Delete reservation',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DELETE],
  });
  const eTransportFlag = useSecuredFlag('reservation-detail-feature-etransport', {
    kind: 'feature',
    description: 'eTransport (RO) tab',
    features: [FeaturesEnum.ETRANSPORT_RO],
  });
  const orderDiscrepancyFlag = useSecuredFlag('reservation-detail-feature-order-discrepancy', {
    kind: 'feature',
    description: 'Order discrepancy management',
    features: [FeaturesEnum.ORDER_DISCREPANCY],
  });
  const truckTypeFlag = useSecuredFlag('reservation-detail-feature-truck-type', {
    kind: 'feature',
    description: 'Truck type tag (feature or per-customer setting)',
    features: [FeaturesEnum.RESERVATION_TRUCK_TYPE],
  });
  const canEdit = editFlag.granted;

  useEffect(() => {
    if (isNullOrUndefined(reservation)) return;
    getCreateEditReservationSettings(reservation.customerId, d => {
      setSettings(d);
    });
  }, [reservation]);

  const isArchived = reservation?.isArchived === true;
  const canEditAttachments = canEdit && !isArchived;

  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent } = useCreateAttachmentComponent(
    {
      enableCopyDocument: false,
      initialAttachments: null,
      doNotShare: !canEditAttachments,
      disabled: !canEditAttachments,
      enableDownloadZip: true,
      downloadZipApplicationId: reservation?.applicationId,
      // Attachment changes don't go through load(), so the page beside an embedded detail is told here.
      onChanged,
    },
    {
      entityType: EntityTypeEnum.TIMESLOT,
      entityId: reservationId,
      disabled: !canEditAttachments,
    },
  );

  const { ReservationChangeSlotDuration, setVisible: setVisibleChangeDurationDialog, visible: visibleChangeDuration } = useCreateChangeSlotDuration();
  const { setTimeslotIdForDuplication, DuplicateTimeslot } = useDuplicateTimeslot();

  const navigation = useNavigation();

  const isOwner = reservation?.customerId == loggedUser.companyId;
  // RD-2977: folders tag only when the user may file into at least one reservation folder (or it is already filed)
  const accessibleFolders = useFolders(EntityTypeEnum.TIMESLOT);
  // Reservation audit: NEW = collapsed behind a click, ORIGINAL = expanded (default).
  const reservationAuditCollapsed = useBetaVersion(BetaSurface.RESERVATION_AUDIT_LOG).version === DefaultVersionEnum.NEW;

  const { EditReservationOrdersSidebar, setVisible: setVisibleOrdersSidebar, visible: visibleOrdersSidebar } = useCreateEditReservationOrdersSidebar();

  const {
    EditReservationOrderItemsDiscrepancySidebar,
    setVisible: setVisibleOrderItemsDiscrepancySidebar,
    visible: visibleOrderItemsDiscrepancySidebar,
  } = useCreateEditReservationOrderItemsDiscrepancySidebar();

  const showEtransport = eTransportFlag.granted;
  const orderDiscrepancyEnabled = orderDiscrepancyFlag.granted;
  const onBehalfOfEnabled = settings?.onBehalfOfEnabled === true;
  const driverNameRequired = isOwner ? settings?.driverNameRequired === true : settings?.driverNameRequiredCounterParty === true;
  const driverPhoneRequired = isOwner ? settings?.driverPhoneRequired === true : settings?.driverPhoneRequiredCounterParty === true;
  const driverIdRequired = isOwner ? settings?.driverIdRequired === true : settings?.driverIdRequiredCounterParty === true;
  const carPlateRequired = isOwner ? settings?.carPlateRequired === true : settings?.carPlateRequiredCounterParty === true;

  useLayoutEffect(() => {
    const ac = new AbortController();
    if (isNotNullOrUndefined(reservationId)) {
      load();
      loadAttachments(EntityTypeEnum.TIMESLOT, reservationId);
    }
    return () => {
      ac.abort();
    };
  }, [reservationId]);

  // The first load of a reservation is not a change; every later one follows an edit (all edit paths
  // end in load()), which is when `onChanged` tells the page around an embedded detail.
  const loadedOnceRef = useRef<string | null>(null);
  const load = () => {
    getReservationDetail(reservationId, d => {
      setReservation(d);
      if (loadedOnceRef.current === reservationId) onChanged?.();
      loadedOnceRef.current = reservationId;
      // "Send webhook" menu item: only the owner company can have definitions, so skip the call for the carrier side.
      if (d.customerId == loggedUser.companyId) {
        manualWebhookReservationAvailability(reservationId, a => setWebhookAvailable(a.available), null, {
          successToast: false,
          onError: () => setWebhookAvailable(false),
        });
      } else {
        setWebhookAvailable(false);
      }
      // Every reservation mutation funnels through load(); refresh the audit table too so edits appear
      // without a remount. No-op while the lazy audit section is unmounted (ref null).
      auditLogRef.current?.reload();
      isEntityManagedByWorkflow(reservationId, EntityTypeEnum.TIMESLOT, d => {
        setIsManagedByWorkflow(d.isManagedByWF);
      });
    });
  };

  const notifyDriver = (reservationId: string) => {
    reservationNotifyDriver(
      reservationId,
      {
        drivePhone: 'cislodotahnemezrezervaceTODO',
      },
      () => {
        showToastMessage(
          tr(`ReservationMoreButtons.driverNotified`, `Řidič byl vyzván k příjezdu`),
          tr('ReservationMoreButtons.driverNotifiedDetail', 'Na telefonní číslo uvedené v rezervaci byla odeslána SMS zpráva s odkazem na navigaci.'),
          'success',
        );
      },
    );
  };

  const truckTypeEnabled = truckTypeFlag.granted || settings?.truckTypeEnabled;
  const hasDiscrepancy = reservation?.orders.find(o => o.items.find(item => orderDiscrepancyEnabled && item.discrepancyAmount !== null && item.discrepancyAmount >= 0));
  const isLoading = reservation?.reservationActionTypes.indexOf(ReservationActionTypesEnum.LOADING) > -1;
  const isUnloading = reservation?.reservationActionTypes.indexOf(ReservationActionTypesEnum.UNLOADING) > -1;

  // Partners (counter party) may not edit timeslots/orders once the reservation start is within the configured block window.
  // Composed from individual rules so each condition is inspectable in security mode; granted === blocked.
  const counterPartyEditBlockedBeforeStart = useSecuredFlag('reservation-detail-counterparty-edit-blocked-before-start', {
    kind: 'editability',
    description: 'Counter party edit of timeslot/orders blocked within the configured window before reservation start',
    state: [
      { label: 'user is counter party (partner)', satisfied: !isOwner },
      { label: 'counter party edit block configured', satisfied: settings?.counterPartyEditBlockedMinutesBeforeStart > 0 },
      {
        label: 'reservation start within block window',
        satisfied:
          isNotNullOrUndefined(reservation?.slot?.utcStart) &&
          dayjsFromApiUtcInTz(reservation.slot.utcStart, reservation.slot.timezoneId).diff(dayjs().tz(reservation.slot.timezoneId), 'minute') < settings?.counterPartyEditBlockedMinutesBeforeStart,
      },
    ],
  });

  if (isNullOrUndefined(reservation)) return <></>;

  // Archived reservations are read-only: gate every edit affordance below on this clause.
  const notArchivedClause = {
    type: 'state' as const,
    label: 'reservation is not archived',
    satisfied: !reservation.isArchived,
    description: 'Read-only once the reservation is archived',
    byDesign: true,
  };

  return (
    <>
      {!embedded && (
      <BreadCrumbComponent
        items={[
          {
            label: tr(`ReservationDetail.bcReservationList`, `Seznam rezervací`),
            url: navigation.createNavigationLink(navigation.urlFunctions.createReservationList()),
          },
          { label: tr(`ReservationDetail.bcReservationDetail`, `Detail`) },
        ]}
      />
      )}
      <Heading
        title={reservation.applicationId}
        // Embedded, the number is the way to the full page.
        titleLink={embedded ? navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(reservation.id)) : undefined}
        headingVariant={embedded ? 'h2' : 'h1'}
        labels={
          <>
            <SecuredArea
              id="reservation-detail-ramp-pin"
              label="Pinned on ramp table"
              description="Icon showing that the reservation is pinned on the location's ramp table"
              visible={[
                { type: 'data', label: 'pinned on ramp table', satisfied: reservation.hasTable && reservation.isPinnedOnLocRampTable },
                { type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: only the company that owns the reservation sees the ramp table pin', byDesign: true },
              ]}>
              <SvgReservationPin className="ml-2 shrink-0" />
            </SecuredArea>
            <LabelsTag
              objectId={reservation.id}
              objectType={EntityTypeEnum.TIMESLOT}
              resolveLabelsForType={EntityTypeEnum.TIMESLOT}
              secured={{
                id: 'reservation-detail-labels',
                label: 'Labels tag',
                description: "The reservation owner's labels on the reservation",
                visible: [{ type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: labels belong to the company that owns the reservation', byDesign: true }],
                editable: [editFlag, notArchivedClause],
              }}
            />
          </>
        }
        buttons={[
          <ReservationMoreButtons
            key="more-buttons"
            reloadHandler={() => load()}
            locationId={reservation.locationId}
            reservationId={reservation.id}
            reservationAppId={reservation.applicationId}
            isPinnedOnLocRampTable={reservation.isPinnedOnLocRampTable}
            hasPhone={reservation.driverInfo?.driverPhone != null}
            hasTable={reservation.hasTable}
            isOwner={isOwner}
            webhookAvailable={webhookAvailable}
            onCopyReservation={reservationId => {
              setTimeslotIdForDuplication(reservationId);
            }}
            onChangeDuration={() => {
              setVisibleChangeDurationDialog(true);
            }}
            onDuplicate={() => openDuplicate()}
            hasTimeslot={reservation.slot?.utcStart != null}
            onNotifyDriver={id => notifyDriver(id)}
            driverPhoneNumber={reservation.driverInfo?.driverPhone}
            rampName={reservation.rampName}
            location={reservation.locationName + ' ' + reservation.locationCity}
            since={reservation.slot ? formatDateTimeInTzWithLocale(reservation.slot.utcStart, reservation.slot.timezoneId) : ''}
            onDelete={() => {
              // Embedded, the list is already on screen: refresh it, and the deleted row's detail goes with it.
              if (embedded) onChanged?.();
              else navigation.navigate(navigation.urlFunctions.createReservationList());
            }}
            isArchived={reservation.isArchived}
            locationDeleted={locationState.deletedWithoutReplacement}
            editBlockedBeforeStart={counterPartyEditBlockedBeforeStart.granted}
            canDelete={settings ? ((settings.ownerCanDelete && isOwner) || (settings.counterPartyCanDelete && !isOwner)) && deleteFlag.granted : false}
            dataTestId='reservationDetail-moreButton'
            secured={{ id: 'reservation-detail-more-actions', label: 'More-actions menu', description: 'Menu with more actions on the reservation (duplicate, change duration, archive, delete and others)', visible: editFlag }}
          />,
        ]}
      />

      <SecuredArea
        id="reservation-detail-audit-highlights"
        label="Audit highlights"
        description="Highlights from the reservation history: messages sent to the driver and the driver's replies"
        visible={[{ type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: shown only to the company that owns the reservation', byDesign: true }, auditReadFlag]}>
        {/* Highlights read the audit log itself — without the audit read permission the query 403s. */}
        <AuditLogHighlights
          entityId={reservationId}
          actions={[
            AuditActionEnum.WHATSAPP_TIMESLOT_GO_TO_RAMP_SENT,
            AuditActionEnum.WHATSAPP_TIMESLOT_ON_TIME_CHECK_SENT,
            AuditActionEnum.WHATSAPP_TIMESLOT_ON_TIME_CHECK_REPLY,
          ]}
        />
      </SecuredArea>
      <div className="ringil-badge-row mb-2 md:mb-4">
        <LinkedExpBadge expeditionCoords={reservation.linkedToExpCoords} />
        <CreatedBadge createdAt={reservation.createdAt} createdBy={reservation.createdBy} timezoneId={loggedUser.defaultTimezoneId} dataTestId='reservationInfo-createdAt'/>
        <UpdatedBadge updatedAt={reservation.updatedAt} updatedBy={reservation.updatedBy} timezoneId={loggedUser.defaultTimezoneId} dataTestId='reservationInfo-updatedAt'/>
      </div>

      <div className="ringil-badge-row mb-2 md:mb-4">
        {/* Only the workflow owner (customer) sees/manages the workflow status. Mount the tag only
            when it would be visible anyway — otherwise the component still runs its body for a carrier
            (reads state.additionalField, fires getWorkflowPossibleTransition) and crashes on a null state. */}
        {isManagedByWorkflow && isOwner && (
          <WorkflowStateTag
            state={reservation.state}
            entityId={reservation.id}
            entityType={EntityTypeEnum.TIMESLOT}
            onReload={() => {
              load();
            }}
            secured={{
              id: 'reservation-detail-workflow-state',
              label: 'Workflow state tag',
              description: "The reservation's workflow status, which the reservation owner moves on",
              visible: [
                { type: 'state', label: 'managed by workflow', satisfied: isManagedByWorkflow, description: 'Shown only when the reservation is managed by a status workflow', byDesign: true },
                { type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: the partner sees the status read-only, if at all', byDesign: true },
              ],
              editable: [editStatusFlag, notArchivedClause],
            }}
          />
        )}
        {/* Partner (counter-party) sees the status read-only when the owner enabled it (BE only sends
            state in that case). Same tag as the owner, but no edit action / transition fetch.
            SecuredArea returns null when not visible, so the tag never mounts for the owner or a
            stateless reservation (would otherwise read a null state and crash). */}
        <SecuredArea
          id="reservation-detail-workflow-state-readonly"
          label="Workflow state (read-only)"
          description="The reservation's workflow status as the partner sees it, read-only"
          visible={[
            { type: 'state', label: 'managed by workflow', satisfied: isManagedByWorkflow, description: 'Shown only when the reservation is managed by a status workflow', byDesign: true },
            { type: 'ownership', label: 'user is counter party (partner)', satisfied: !isOwner, description: 'Partner only: the reservation owner sees the editable status instead', byDesign: true },
            { type: 'data', label: 'reservation has state', satisfied: isNotNullOrUndefined(reservation.state) },
          ]}>
          <WorkflowStateTag state={reservation.state} entityId={reservation.id} entityType={EntityTypeEnum.TIMESLOT} onReload={() => load()} readOnly />
        </SecuredArea>
        <ReservationNameTag
          reservationName={reservation.reservationName}
          onChange={v => {
            reservationUpdateReservationName(reservation.id, { reservationName: v }, () => {
              load();
            });
          }}
          dataTestId='reservationInfo-reservationName'
          secured={{ id: 'reservation-detail-tag-name', label: 'Reservation name tag', description: 'Name of the reservation', editable: [editFlag, notArchivedClause] }}
        />
        <CustomerNameTag customerName={reservation.customerName} dataTestId='reservationInfo-customerName' />
      </div>
      <div className="ringil-badge-row">
        <TruckTag
          truckTypeCode={reservation.truck?.code}
          onChange={v => reservationUpdateTruck(reservationId, { truckTypeCode: v }, () => load())}
          secured={{
            id: 'reservation-detail-truck-tag',
            label: 'Truck type tag',
            description: 'Truck type of the reservation',
            visible: [
              {
                type: 'custom',
                label: 'feature RESERVATION_TRUCK_TYPE OR settings.truckTypeEnabled',
                satisfied: !!truckTypeEnabled,
                description: "Shown only when truck type is turned on: the RESERVATION_TRUCK_TYPE feature, or the reservation owner's reservation settings",
                byDesign: false,
              },
            ],
            editable: [editFlag, notArchivedClause],
          }}
        />
        <InternalInfoCatalogTag
          type={CodeTypeEnum.ON_BEHALF_OF}
          inheritorId={reservation.customerId}
          value={reservation.onBehalfOf?.id}
          onChange={v => {
            updateReservationOnBehalfOf(reservation.id, { onBehalfOfCodeListId: v ?? null }, () => {
              load();
            });
          }}
          secured={{
            id: 'reservation-detail-on-behalf-of',
            label: 'On behalf of tag',
            description: "The company the reservation is made on behalf of, from the reservation owner's code list",
            visible: [
              {
                type: 'custom',
                label: 'settings.onBehalfOfEnabled',
                satisfied: onBehalfOfEnabled,
                description: "Shown only when 'on behalf of' is turned on in the reservation owner's reservation settings",
                byDesign: false,
              },
            ],
            editable: [editFlag, notArchivedClause],
          }}
        />
        <ReservationNoteTag
          reservationNote={reservation.reservationNote}
          onChange={v => {
            reservationUpdateNote(reservation.id, { reservationNote: v }, () => {
              load();
            });
          }}
          dataTestId='reservationInfo-reservationNote'
          secured={{ id: 'reservation-detail-tag-note', label: 'Reservation note tag', description: 'Note on the reservation', editable: [editFlag, notArchivedClause] }}
          maxLength={4000}
        />
      </div>

      {renderTabs({ showEtransport, showInternalInfo: isOwner || auditReadFlag.granted })}

      {selectedPanel === 'reservation' && (
        <>
          <div className="mb-4">
            <COHeadline variant={'h2'} title={tr(`ReservationDetail.carrier`, `Carrier`)} icon={<SvgLkw />} className="mb-5"></COHeadline>

            <div className="ringil-badge-row mb-2 md:mb-4 max-xs:!flex-col">
              <SelectedCarrierTag
                carrierName={reservation.carrierName}
                carrierId={reservation.carrierId}
                companyLocationCustomerId={reservation.customerId}
                onChange={carrierId => {
                  updateReservationCarrierEntity(reservation.id, { carrierId }, () => {
                    load();
                  });
                }}
                dataTestId='ringilTab-shipment-carrier-carrierName'
                secured={{ id: 'reservation-tab-tag-carrier', label: 'Carrier tag', description: 'Carrier of the reservation', editable: [editFlag, notArchivedClause] }}
              />
              <CarPlateTag
                carPlate={reservation.carPlate}
                onChange={v => {
                  reservationUpdateCarPlate(reservation.id, { carPlate: v }, () => {
                    load();
                  });
                }}
                dataTestId='ringilTab-shipment-carrier-carPlate'
                required={carPlateRequired}
                secured={{ id: 'reservation-tab-tag-car-plate', label: 'Car plate tag', description: 'Car plate of the vehicle coming to the timeslot', editable: [editFlag, notArchivedClause] }}
                maxLength={255}
              />
              <DriverAndIdTag
                driverName={reservation.driverInfo?.driverName}
                driverId={reservation.driverInfo?.driverId}
                onChange={(driverId, driverName) => {
                  reservationUpdateDriverNameAndID(reservation.id, { driverId, driverName }, () => {
                    load();
                  });
                }}
                dataTestId='ringilTab-shipment-carrier-driverName'
                driverNameRequired={driverNameRequired}
                driverIdRequired={driverIdRequired}
                secured={{ id: 'reservation-tab-tag-driver', label: 'Driver name/id tag', description: 'Name and ID of the driver', editable: [editFlag, notArchivedClause] }}
                maxLength={255}
              />
              <PhoneTag
                phone={reservation.driverInfo?.driverPhone}
                onChange={phone => {
                  reservationUpdateDriverPhone(reservation.id, { driverPhone: phone }, () => {
                    load();
                  });
                }}
                dataTestId='ringilTab-shipment-carrier-driverPhone'
                required={driverPhoneRequired}
                secured={{ id: 'reservation-tab-tag-phone', label: 'Driver phone tag', description: 'Phone number of the driver', editable: [editFlag, notArchivedClause] }}
                maxLength={255}
              />
            </div>
            <div className="ringil-badge-row">
              <ReservationCarrierNote
                note={reservation.carrierNote}
                onChange={v =>
                  reservationUpdateNoteForCarrier(reservation.id, { carrierNote: v }, () => {
                    load();
                  })
                }
                dataTestId='ringilTab-shipment-carrier-noteForCarrier'
                secured={{ id: 'reservation-tab-tag-carrier-note', label: 'Carrier note tag', description: 'Note for the carrier', editable: [editFlag, notArchivedClause] }}
                maxLength={4000}
              />
            </div>
          </div>

          <div className="bg-base-base pt-4 pb-8 md:px-4 md:py-8 px-4 w-[calc(100%+2rem)] -ml-4 rounded-lg">
            <COHeadline className="mb-4" variant={'h2'} title={tr(`ReservationDetail.reservation`, `Reservation`)} icon={<SvgReservation />}></COHeadline>

            <div className="mb-4">
              <ReservationFlagsTag
                flags={reservation.flags ?? []}
                flagsConfig={settings?.flags}
                orders={reservation.orders ?? []}
                onChange={next => {
                  updateReservationFlags(reservation.id, { flags: toEditableFlags(next) }, () => {
                    load();
                  });
                }}
                dataTestId="ringilTab-shipment-reservation-flags"
                secured={{ id: 'reservation-tab-tag-flags', label: 'Reservation flags tag', description: "Flags on the reservation, as configured in the reservation owner's settings", editable: [editFlag, notArchivedClause] }}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <ColWithGap>
                  <div data-testid='ringilTab-shipment-reservation-locationName'>
                    <EditableHeading3
                      secured={{
                        id: 'reservation-detail-edit-timeslot',
                        label: 'Edit timeslot',
                        description: 'Change the location, ramp and time of the reservation',
                        visible: [editFlag, notArchivedClause, { type: 'state', label: 'edit not blocked before start. NOT reservation-detail-counterparty-edit-blocked-before-start', satisfied: !counterPartyEditBlockedBeforeStart.granted, description: "A partner cannot change the reservation within the period before its start set in the reservation owner's settings", byDesign: false }],
                      }}
                      title={reservation.locationName}
                      icon={<SvgMappoint />}
                      onEditClick={() => {
                        setVisible(true);
                      }}
                    />
                  </div>
                  <ColWithSmallerGap data-testid='ringilTab-shipment-reservation-address'>
                    {formatLocationNotino(reservation.locationPostalCode, reservation.locationCity, reservation.locationCountry, reservation.locationStreetNr)}
                  </ColWithSmallerGap>

                  <div className="h-[1px] bg-gray-200 md:mt-4"></div>

                  <div data-testid='ringilTab-shipment-reservation-manipulation'>
                    <EditableHeading3
                      editable={false}
                      title={tr(`ReservationDetail.plannedManipulation`, `Planned manipulation`)}
                      icon={<SvgCalendar />}
                    />
                      <ColWithSmallerGap data-testid='ringilTab-shipment-reservation-manipulation-timeslot'>
                        {stringFromTimeslotResponse(reservation.slot)}
                        <COTimezone dataTestId='ringilTab-shipment-reservation-manipulation-timezone' timezoneId={reservation.slot?.timezoneId} />
                        <div data-testid='ringilTab-shipment-reservation-manipulation-ramp'>{reservation.rampName}</div>
                      </ColWithSmallerGap>
                  </div>
                </ColWithGap>
              </Card>
              <Card>
                <DiscrepancyEditHeading3
                  secured={{
                    id: 'reservation-detail-edit-orders',
                    label: 'Edit orders',
                    description: 'Change the orders and handling units of the reservation',
                    visible: [editFlag, notArchivedClause, { type: 'state', label: 'edit not blocked before start. NOT reservation-detail-counterparty-edit-blocked-before-start', satisfied: !counterPartyEditBlockedBeforeStart.granted, description: "A partner cannot change the reservation within the period before its start set in the reservation owner's settings", byDesign: false }],
                  }}
                  title={tr(`ReservationDetail.manipulation`, `Manipulation`)}
                  icon={<SvgReservationManipulation className="text-content-secondary" />}
                  onEditClick={() => {
                    setVisibleOrdersSidebar(true);
                  }}
                  onDiscrepancyUpdateClick={() => {
                    setVisibleOrderItemsDiscrepancySidebar(true);
                  }}
                />
                <>
                  <div>
                    <SecuredArea
                      id="reservation-detail-discrepancy-note"
                      label="Discrepancy note"
                      visible={[{ type: 'data', label: 'reservation has order discrepancy', satisfied: isNotNullOrUndefined(hasDiscrepancy) }]}
                    >
                      <div className="p-4 bg-content-brandMedium rounded mb-5 flex justify-start gap-4 items-center">
                        <div className="text-content-tertiary">
                          <SvgExclamationFilled />
                        </div>
                        <p className="text-zinc-800 text-xs font-normal">
                          {tr('ReservationDetail.discrepancyNote', 'U této manipulace evidujete nesoulad v objednávkách.')}
                        </p>
                      </div>
                    </SecuredArea>

                    {(isUnloading || isLoading) && (
                      <div className="flex flex-row gap-4 mb-3">
                        <SecuredArea
                          id="reservation-detail-action-loading"
                          label="Loading badge"
                          visible={[{ type: 'data', label: 'reservation action is loading', satisfied: isLoading }]}
                        >
                          <div data-testid='ringilTab-shipment-reservation-loading'>
                            <COBadge type={'loading'} variant={'filled'}>
                              {tr(`ReservationDetail.loading`, `Loading`)}
                            </COBadge>
                          </div>
                        </SecuredArea>
                        <SecuredArea
                          id="reservation-detail-action-unloading"
                          label="Unloading badge"
                          visible={[{ type: 'data', label: 'reservation action is unloading', satisfied: isUnloading }]}
                        >
                          <div data-testid='ringilTab-shipment-reservation-unloading'>
                            <COBadge type={'unloading'} variant={'filled'}>
                              {tr(`ReservationDetail.unloading`, `Unloading`)}
                            </COBadge>
                          </div>
                        </SecuredArea>
                      </div>
                    )}

                    <div className="flex flex-col gap-y-6">
                      {reservation?.orders.map((order, orderIndex) => {
                        return (
                          <RingilOrder
                            key={orderIndex}
                            type={'neutral'}
                            order={order}
                            showDiscrepancy={orderDiscrepancyEnabled}
                            showReturnablePackage={reservation.flags?.some(f => f.reservationFlagType === ReservationFlagTypeEnum.RETURNABLE_PACKAGE) ?? false}
                            dataTestIdPrefix='ringilTab-shipment-reservation'
                            orderIndex={orderIndex}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              </Card>
              {visible && (
                <EditReservationSlotSidebar
                  applicationId={reservation.applicationId}
                  companyLocationCustomerId={reservation.customerId}
                  reservationId={reservation.id}
                  onRefresh={() => {
                    load();
                  }}
                  expeditionLocationId={reservation.locationId}
                />
              )}
              {visibleOrdersSidebar && (
                <EditReservationOrdersSidebar
                  applicationId={reservation.applicationId}
                  reservationId={reservation.id}
                  onRefresh={() => {
                    load();
                  }}
                  companyLocationCustomerId={reservation.customerId}
                />
              )}
              {visibleOrderItemsDiscrepancySidebar && (
                <EditReservationOrderItemsDiscrepancySidebar
                  applicationId={reservation.applicationId}
                  reservationId={reservation.id}
                  onRefresh={() => {
                    load();
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
      {selectedPanel === 'eTransport' && (
        <ETransport
          reservationId={reservationId}
          applicationId={reservation.applicationId}
          secured={{
            id: 'reservation-detail-panel-etransport',
            label: 'eTransport panel',
            description: 'Romanian e-Transport declaration of the reservation',
            visible: eTransportFlag,
            editable: [editFlag, notArchivedClause],
          }}
        />
      )}
      {selectedPanel === 'internalinfo' && (
        <>
          {/* Audit: permission-gated, shown to owner and carrier alike (BE row-filters). */}
          <SecuredArea id="reservation-detail-audit-log" label="Audit log" description="History of all changes made to the reservation" visible={auditReadFlag}>
            {reservationAuditCollapsed ? (
              <SectionPanel
                icon={<SvgAuditlog />}
                heading={tr(`AuditLog.heading`, `Audit log`)}
                deferred={{
                  description: tr('AuditLog.revealHint', 'All actions performed by you or your colleagues.'),
                  revealLabel: tr('AuditLog.revealLabel', 'Show action history'),
                  dataTestId: 'reservationDetail-reveal-auditLog',
                }}>
                <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.TIMESLOT} entityId={reservationId} hideHeader />
              </SectionPanel>
            ) : (
              <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.TIMESLOT} entityId={reservationId} />
            )}
          </SecuredArea>
          {/* Internal note: owner-only — a single owner field the BE does not party-filter. */}
          <SecuredArea
            id="reservation-detail-internal-note-section"
            label="Internal note"
            description="The reservation owner's internal note; the partner never sees it"
            visible={[{ type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: the internal note belongs to the company that owns the reservation', byDesign: true }]}>
            <COHeadline variant={'h3'} title={tr(`ReservationDetail.interniInfo`, `Interní info`)} />
            <Ringil3BadgeRow>
              <InternalInfoFoldersTag
                folders={(reservation.folders ?? []).map(f => ({ id: f.folderId, name: f.folderName, active: true }))}
                required={settings?.foldersRequired === true}
                onChange={v => {
                  reservationUpdateFolders(reservationId, { folderIds: v.map(f => f.id) }, () => {
                    load();
                  });
                }}
                entityType={EntityTypeEnum.TIMESLOT}
                secured={{
                  id: 'reservation-detail-tag-folders',
                  label: 'Folders tag',
                  visible: [
                    { type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner },
                    { type: 'data', label: 'user has reservation folders or the reservation is filed', satisfied: accessibleFolders.length > 0 || (reservation.folders?.length ?? 0) > 0 },
                  ],
                  editable: [editInternalNoteFlag, notArchivedClause],
                }}
              />
              <InternalNoteTag
                value={reservation.internalNote}
                onChange={v => {
                  reservationUpdateInternalNote(reservationId, { internalNote: v }, () => {
                    load();
                  });
                }}
                secured={{
                  id: 'reservation-detail-tag-internal-note',
                  label: 'Internal note tag',
                  description: "The reservation owner's internal note",
                  visible: [{ type: 'ownership', label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: the internal note belongs to the company that owns the reservation', byDesign: true }],
                  editable: [editInternalNoteFlag, notArchivedClause],
                }}
              />
            </Ringil3BadgeRow>
          </SecuredArea>
        </>
      )}
      {selectedPanel === 'attachments' && (
        <>
          <AttachmentComponent internals={attachmentInternals} entityType={EntityTypeEnum.TIMESLOT} entityId={reservationId} disabled={!canEditAttachments} />
        </>
      )}

      {visibleChangeDuration && (
        <ReservationChangeSlotDuration
          localDate={reservation?.slot?.localDate}
          sinceHourString={reservation?.slot?.sinceHourString}
          tillHourString={reservation?.slot?.tillHourString}
          timeZone={reservation?.slot?.timeZone}
          utcStart={reservation?.slot?.utcStart}
          utcEnd={reservation?.slot?.utcEnd}
          timezoneId={reservation?.slot?.timezoneId}
          calendarId={reservation?.calendarId}
          reservationId={reservationId}
          onComplete={() => {
            load();
          }}
        />
      )}
      <DuplicateTimeslot
        onComplete={() => {}}
        customerId={reservation.customerId}
        replacementLocation={locationState.replacement ? { locationId: locationState.replacement.id, locationName: locationState.replacement.name } : undefined}
      />
      {createTimeslotRequest && (
        <CreateTimeslotSidebar
          onContinue={() => {}}
          initialData={{
            actionTypes: reservation.reservationActionTypes,
            driverInfo: reservation.driverInfo,
            companyLocationCustomerId: reservation.customerId,
            customerCompanyCode: null,
            carPlate: reservation.carPlate,
            orders: reservation.orders.map(t => ({
              orderAmount: t.orderAmount,
              items: t.items.map(t => ({
                amount: t.amount,
                itemNote: t.itemNote,
                cargoTemplateCode: t.cargoTemplate?.code,
                stackable: t.stackable,
                decimalPlaces: t.cargoTemplate?.decimalPlaces,
                height: t.height,
                width: t.width,
                length: t.length,
                weight: t.weight,
              })),
              orderValue: t.orderValue,
              orderName: t.orderName,
              orderNote: t.orderNote,
              orderDescription: t.orderDescription,
            })),
            reservationName: reservation.reservationName,
            reservationNote: reservation.reservationNote,
            carrierId: reservation.carrierId,
            companyLocationId: copyTargetLocationId,
            carrierNote: reservation.carrierNote,
            timeslot: {
              localDate: dayjs().format('YYYY-MM-DD'),
              arrivalHourString: '',
              durationInMin: '0',
              rampId: null,
              timezoneId: reservation.slot?.timezoneId,
            },
            createExpedition: false,
            truckType: reservation.truck?.code,
            flags: reservation.flags,
            onBehalfOfCodeListId: reservation.onBehalfOf?.id,
            sendNotification: true,
          }}
        />
      )}
    </>
  );
};

export default ReservationDetail;
